/**
 * IndexedDB Storage Layer for Multi-Account Support
 * Mirrors the extension's storage model.
 */

import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import { createScopedClient } from './api';

// =============================================================================
// Types
// =============================================================================

export interface Account {
    email: string;
    deviceKey: { id: string; secret: string };
    mailboxKey?: string; // Base64 encoded Mailbox Key
    addedAt: number;
    lastUsedAt: number;
    keyStatus: 'valid' | 'revoked' | 'unknown';
}

export interface Message {
    id: string;
    sender: string;
    subject: string;
    preview?: string;
    received_at: string;
    decrypted?: boolean;
}

export type AppStatus = 'setup_required' | 'idle' | 'listening';
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export interface AppState {
    accounts: Account[];
    activeAccountEmail: string | null;
    messages: Record<string, Message[]>;
    status: AppStatus;
    connectionStatus: ConnectionStatus;
    listeningExpiresAt?: number | undefined;
}

// =============================================================================
// IndexedDB Schema
// =============================================================================

interface AntennaDB extends DBSchema {
    state: {
        key: string;
        value: unknown;
    };
}

const DB_NAME = 'sisumail-antenna';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<AntennaDB>> | null = null;

function getDB() {
    if (!dbPromise) {
        dbPromise = openDB<AntennaDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                db.createObjectStore('state');
            },
        });
    }
    return dbPromise;
}

// =============================================================================
// Storage Helpers
// =============================================================================

import { API_BASE_URL } from '@sisumail/config';

const API_URL = import.meta.env.VITE_API_URL || API_BASE_URL;
const MAX_ACCOUNTS = 20;

/**
 * Get or create a stable device fingerprint.
 * This persists across app reinstalls (as long as IndexedDB isn't cleared).
 */
export async function getDeviceFingerprint(): Promise<string> {
    const db = await getDB();
    let fingerprint = (await db.get('state', 'deviceFingerprint')) as string | undefined;

    if (!fingerprint) {
        fingerprint = crypto.randomUUID();
        await db.put('state', fingerprint, 'deviceFingerprint');
    }

    return fingerprint;
}

export async function getState(): Promise<AppState> {
    const db = await getDB();
    const accounts = (await db.get('state', 'accounts')) as Account[] | undefined;
    const activeAccountEmail = (await db.get('state', 'activeAccountEmail')) as
        | string
        | null
        | undefined;
    const messages = (await db.get('state', 'messages')) as Record<string, Message[]> | undefined;
    const status = (await db.get('state', 'status')) as AppStatus | undefined;
    const connectionStatus = (await db.get('state', 'connectionStatus')) as
        | ConnectionStatus
        | undefined;
    const listeningExpiresAt = (await db.get('state', 'listeningExpiresAt')) as number | undefined;

    return {
        accounts: accounts || [],
        activeAccountEmail: activeAccountEmail ?? null,
        messages: messages || {},
        status: status || 'setup_required',
        connectionStatus: connectionStatus || 'disconnected',
        listeningExpiresAt,
    };
}

export async function setState(updates: Partial<AppState>): Promise<void> {
    const db = await getDB();
    const tx = db.transaction('state', 'readwrite');

    for (const [key, value] of Object.entries(updates)) {
        await tx.store.put(value, key);
    }

    await tx.done;
}

export async function getActiveAccount(): Promise<Account | null> {
    const state = await getState();
    if (!state.activeAccountEmail || state.accounts.length === 0) return null;
    return (
        state.accounts.find((a) => a.email === state.activeAccountEmail) ||
        state.accounts[0] ||
        null
    );
}

export async function setActiveAccount(email: string): Promise<boolean> {
    const state = await getState();
    const account = state.accounts.find((a) => a.email === email);
    if (!account) return false;

    account.lastUsedAt = Date.now();
    await setState({
        activeAccountEmail: email,
        accounts: state.accounts,
    });
    return true;
}

export async function addAccount(account: Account): Promise<{ success: boolean; error?: string }> {
    const state = await getState();

    if (state.accounts.length >= MAX_ACCOUNTS) {
        return {
            success: false,
            error: `Maximum ${MAX_ACCOUNTS} accounts allowed`,
        };
    }

    if (state.accounts.some((a) => a.email === account.email)) {
        return { success: false, error: 'Account already connected' };
    }

    state.accounts.push(account);
    state.messages[account.email] = [];

    const isFirst = state.accounts.length === 1;

    await setState({
        accounts: state.accounts,
        messages: state.messages,
        activeAccountEmail: isFirst ? account.email : state.activeAccountEmail,
        status: 'idle',
    });

    return { success: true };
}

export async function removeAccount(email: string): Promise<void> {
    const state = await getState();
    const index = state.accounts.findIndex((a) => a.email === email);
    if (index === -1) return;

    // Server-side revocation
    const account = state.accounts[index];
    if (account?.deviceKey) {
        try {
            const client = createScopedClient(account.deviceKey);
            await client.devices.revoke(account.deviceKey.id);
        } catch (_e) {
            // Proceed with local removal anyway
        }
    }

    state.accounts.splice(index, 1);
    delete state.messages[email];

    let newActiveEmail: string | null = state.activeAccountEmail;
    if (state.activeAccountEmail === email) {
        newActiveEmail = state.accounts[0]?.email || null;
    }

    await setState({
        accounts: state.accounts,
        messages: state.messages,
        activeAccountEmail: newActiveEmail,
        status: state.accounts.length === 0 ? 'setup_required' : state.status,
    });
}

export async function markAccountKeyStatus(
    email: string,
    status: Account['keyStatus']
): Promise<void> {
    const state = await getState();
    const account = state.accounts.find((a) => a.email === email);
    if (account) {
        account.keyStatus = status;
        await setState({ accounts: state.accounts });
    }
}

export async function updateMessages(email: string, messages: Message[]): Promise<void> {
    const state = await getState();
    state.messages[email] = messages;
    await setState({ messages: state.messages });
}

export async function addMessage(email: string, message: Message): Promise<boolean> {
    const state = await getState();
    const accountMessages = state.messages[email] || [];

    // Deduplicate
    if (accountMessages.find((m) => m.id === message.id)) {
        return false;
    }

    state.messages[email] = [message, ...accountMessages];
    await setState({ messages: state.messages });
    return true;
}

export async function clearAllData(): Promise<void> {
    const db = await getDB();
    await db.clear('state');
}
